import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Skeleton,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import dayjs from 'dayjs';
import {
  PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS,
  cancelarBoletoInscritos,
  type PagamentosInscritosLigaFiltradosData,
  type PagamentosInscritosLigaFiltradosVars,
} from '../../../services/pagamentoInscritosLigaService';
import type { PagamentoInscritosLiga } from '../../../types/pagamentoInscritosLiga';
import { StatusPagamento } from '../../../types/pagamento';
import PaymentStatusChip from '../../../components/PaymentStatusChip';
import { formatCurrency } from '../../../utils/money';
import BoletoDetailDialog from '../../../components/liga/BoletoDetailDialog';
import AdminEditPagamentoDialog from './AdminEditPagamentoDialog';

const COLUMN_COUNT = 8;

const STATUS_OPTIONS: { label: string; value: StatusPagamento | null }[] = [
  { label: 'Todos', value: null },
  { label: 'Pago', value: 'PAGO' as StatusPagamento },
  { label: 'Pendente', value: 'PENDENTE' as StatusPagamento },
  { label: 'Cancelado', value: 'CANCELADO' as StatusPagamento },
  { label: 'Estornado', value: 'ESTORNADO' as StatusPagamento },
];

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

interface AdminLigaPagamentosSubTabProps {
  ligaId: string;
  onDataChanged?: () => void;
}

const AdminLigaPagamentosSubTab: React.FC<AdminLigaPagamentosSubTabProps> = ({
  ligaId,
  onDataChanged,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<StatusPagamento | null>(null);

  const [selectedPagamento, setSelectedPagamento] = useState<PagamentoInscritosLiga | null>(null);
  const [editPagamento, setEditPagamento] = useState<PagamentoInscritosLiga | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const { data, loading, error, refetch } = useQuery<
    PagamentosInscritosLigaFiltradosData,
    PagamentosInscritosLigaFiltradosVars
  >(PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS, {
    variables: {
      ligaId,
      status: statusFilter || undefined,
      page,
      size: rowsPerPage,
    },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const pagamentos = data?.pagamentosInscritosLigaFiltrados?.items ?? [];
  const totalItems = data?.pagamentosInscritosLigaFiltrados?.totalItems ?? 0;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter((value || null) as StatusPagamento | null);
    setPage(0);
  };

  const handleResetFilters = () => {
    setStatusFilter(null);
    setPage(0);
  };

  const handleCancel = async (p: PagamentoInscritosLiga) => {
    const confirmar = window.confirm(
      `Tem certeza que deseja cancelar o boleto da categoria "${p.categoriaTorneio.nome}"?`,
    );
    if (!confirmar) return;

    setCancelling(p.id);
    try {
      await cancelarBoletoInscritos(p.id);
      setSnackbar({ open: true, message: 'Boleto cancelado com sucesso.', severity: 'success' });
      await refetch();
      onDataChanged?.();
    } catch (e: any) {
      const msg =
        e?.graphQLErrors?.[0]?.message || e?.message || 'Erro ao cancelar boleto.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setCancelling(null);
    }
  };

  const handleEditSaved = async () => {
    await refetch();
    onDataChanged?.();
    setSnackbar({ open: true, message: 'Pagamento atualizado com sucesso.', severity: 'success' });
  };

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const linkStyle = { color: '#1976d2', textDecoration: 'none' as const };

  return (
    <>
      {/* Filter Bar */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={statusFilter || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || 'all'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: '0 1 auto' }}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleResetFilters}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Torneio</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">
                Inscritos
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Valor</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Vencimento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Pagamento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: COLUMN_COUNT }).map((__, cidx) => (
                    <TableCell key={cidx}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <div className="alert alert-danger mb-0" role="alert">
                    Erro ao carregar cobranças. Tente novamente.
                  </div>
                </TableCell>
              </TableRow>
            ) : pagamentos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  Nenhuma cobrança encontrada.
                </TableCell>
              </TableRow>
            ) : (
              pagamentos.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    {p.categoriaTorneio?.torneio?.id ? (
                      <Link
                        to={`/tournamentsingle/${p.categoriaTorneio.torneio.id}`}
                        style={linkStyle}
                      >
                        {p.categoriaTorneio.torneio.nome}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {p.categoriaTorneio?.id && p.categoriaTorneio?.torneio?.id ? (
                      <Link
                        to={`/tournament/${p.categoriaTorneio.torneio.id}/category/${p.categoriaTorneio.id}`}
                        style={linkStyle}
                      >
                        {p.categoriaTorneio.nome}
                      </Link>
                    ) : (
                      p.categoriaTorneio?.nome || '-'
                    )}
                  </TableCell>
                  <TableCell align="center">{p.quantidadeInscritos}</TableCell>
                  <TableCell>{formatCurrency(p.valor)}</TableCell>
                  <TableCell>
                    <PaymentStatusChip status={p.status} />
                  </TableCell>
                  <TableCell>{formatDate(p.dataVencimento)}</TableCell>
                  <TableCell>{formatDate(p.dataPagamento)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalhes">
                      <IconButton size="small" onClick={() => setSelectedPagamento(p)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => setEditPagamento(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {p.status !== 'CANCELADO' && p.status !== 'PAGO' && (
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancel(p)}
                          disabled={cancelling === p.id}
                        >
                          <CancelOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>

      {/* View Dialog */}
      <BoletoDetailDialog
        open={selectedPagamento !== null}
        pagamento={selectedPagamento}
        onClose={() => setSelectedPagamento(null)}
      />

      {/* Edit Dialog */}
      <AdminEditPagamentoDialog
        open={editPagamento !== null}
        pagamento={editPagamento}
        onClose={() => setEditPagamento(null)}
        onSaved={handleEditSaved}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminLigaPagamentosSubTab;
